// UPPDRAG-40 — DOMPurify-backed HTML sanitization for the RichTextEditor
// output and the player-side renderer. Pure (no I/O), so vitest can pin
// every XSS bypass we care about.
//
// Allow list mirrors what TipTap StarterKit can produce:
//   - block: p, br, h1-h3, blockquote, ul, ol, li, hr
//   - inline: strong, em, b, i, u, s, code, span
//   - links not allowed by default — add later only if needed (each link
//     attribute is an XSS surface). RichTextEditor toolbar has no link
//     button for v1.

import DOMPurify from "dompurify";

export const ALLOWED_TAGS = [
  // Block
  "p", "br", "h1", "h2", "h3", "blockquote", "ul", "ol", "li", "hr", "pre",
  // Inline
  "strong", "em", "b", "i", "u", "s", "code", "span",
];

// No attributes allowed by default — attributes are the most common XSS
// surface (event handlers, javascript: URLs, data: URIs). The TipTap
// toolbar in v1 does not produce any attributes that need to survive
// (no class names, no styles).
export const ALLOWED_ATTR: string[] = [];

/**
 * Sanitize an HTML string for safe rendering via dangerouslySetInnerHTML.
 * Strips all event handlers, javascript: URLs, <script>, <iframe>, etc.
 * Returns trimmed empty string when input is empty/whitespace.
 */
export function sanitizeRichText(html: string | null | undefined): string {
  if (!html || typeof html !== "string") return "";
  const trimmed = html.trim();
  if (trimmed === "" || trimmed === "<p></p>") return "";

  const cleaned = DOMPurify.sanitize(trimmed, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    KEEP_CONTENT: true,
    // Belt-and-suspenders: explicitly forbid the dangerous tag families
    // even though our allow-list already excludes them. DOMPurify warns
    // "an attempt to execute the script content" if these slip through
    // unrelated configuration drift.
    FORBID_TAGS: ["script", "iframe", "object", "embed", "form", "style", "link", "meta"],
    FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onfocus", "onblur", "style"],
  });
  return cleaned;
}

/**
 * True when the rendered string is empty (no visible text or markup).
 * Used by callers to decide whether to render the rules card at all.
 */
export function isRichTextEmpty(html: string | null | undefined): boolean {
  if (!html) return true;
  const cleaned = sanitizeRichText(html);
  if (cleaned === "") return true;
  // Strip tags + collapse whitespace for the empty check.
  const text = cleaned.replace(/<[^>]+>/g, "").replace(/\s+/g, "").trim();
  return text === "";
}
